import React, { useEffect, useState } from "react";
import { Play, X, Video, Heart } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { photoSrc } from "@/components/Stars";
import LikeButton from "@/components/LikeButton";
import PhotographerBadge from "@/components/PhotographerBadge";
import ShareMedia from "@/components/ShareMedia";
import Postcard from "@/components/Postcard";
import ReportButton from "@/components/ReportButton";
import { byLikes } from "@/lib/useLikes";
import { trackEvent } from "@/lib/analytics";

const FADE = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };

function VideoLightbox({ item, close, onLike, countryName }) {
  const { t } = useLang();
  return (
    <motion.div {...FADE} className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-sm flex items-center justify-center" onClick={close} role="dialog" aria-modal="true" data-testid="video-lightbox">
      <button onClick={close} aria-label={t("close")} className="absolute top-4 end-4 w-10 h-10 rounded-full bg-sand/10 hover:bg-sand/25 text-sand flex items-center justify-center" data-testid="video-close">
        <X className="w-5 h-5" />
      </button>
      <figure className="max-w-[92vw] max-h-[88vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <video src={photoSrc(item.video_url)} poster={photoSrc(item.video_poster_url)} controls autoPlay playsInline
          className="max-h-[72vh] max-w-full rounded-xl shadow-2xl bg-black" data-testid="video-player" />
        <figcaption className="mt-3 text-center text-sand">
          <div className="flex items-center justify-center gap-3">
            <div className="text-sm font-semibold flex items-center gap-1.5" data-testid="video-caption-author">{item.author.name} <PhotographerBadge show={item.is_photographer} /> · <span className="text-ochre">{"★".repeat(item.rating)}</span></div>
            <LikeButton item={item} onToggle={onLike} size="md" dark />
            <ShareMedia item={item} countryName={countryName} dark size="md" />
            <Postcard item={item} countryName={countryName} dark size="md" />
            <ReportButton item={item} dark size="md" onHidden={close} />
          </div>
          <p className="text-xs text-sand/70 mt-1 max-w-xl line-clamp-2" dir="auto">{item.text}</p>
        </figcaption>
      </figure>
    </motion.div>
  );
}

function VideoThumb({ v, code, onOpen, onLike }) {
  return (
    <div className="relative shrink-0">
      <button onClick={() => { onOpen(v); trackEvent("video_open", { country_code: code }); }} aria-label={v.author.name} data-testid={`video-thumb-${v.id}`}
        className="relative block w-36 h-24 rounded-xl overflow-hidden border border-stoneLine hover:border-ochre transition-colors group bg-ink">
        {v.video_poster_url ? (
          <img src={photoSrc(v.video_poster_url)} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <video src={photoSrc(v.video_url)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
        )}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-9 h-9 rounded-full bg-sand/90 text-ink flex items-center justify-center shadow group-hover:bg-ochre group-hover:text-white transition-colors">
            <Play className="w-4 h-4 ms-0.5" fill="currentColor" />
          </span>
        </span>
      </button>
      <LikeButton item={v} onToggle={onLike} className="absolute bottom-1 start-1" />
    </div>
  );
}

/** "Visitor videos" strip (most liked first): approved reviews that carry a video, played in a lightbox. */
export default function VideoGallery({ reviews, code, onLike, openId, countryName }) {
  const { t } = useLang();
  const videos = reviews.filter((r) => r.video_url).sort(byLikes);
  const [openedId, setOpenedId] = useState(null);
  const open = videos.find((v) => v.id === openedId);
  const deepLinked = videos.some((v) => v.id === openId);
  useEffect(() => { if (deepLinked) setOpenedId(openId); }, [deepLinked, openId]);
  if (videos.length === 0) return null;

  return (
    <div className="mb-4" data-testid="video-gallery">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">
        <Video className="w-3.5 h-3.5" /> {t("video_gallery")} <span className="text-ochre">({videos.length})</span>
        <span className="ms-auto normal-case tracking-normal flex items-center gap-1"><Heart className="w-3 h-3 text-rose-500" fill="currentColor" /> {t("sorted_by_likes")}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto thin-scroll pb-1 -mx-1 px-1">
        {videos.map((v) => <VideoThumb key={v.id} v={v} code={code} onOpen={(x) => setOpenedId(x.id)} onLike={onLike} />)}
      </div>
      <AnimatePresence>{open && <VideoLightbox item={open} close={() => setOpenedId(null)} onLike={onLike} countryName={countryName} />}</AnimatePresence>
    </div>
  );
}
